import React, { useState, useEffect, useCallback } from "react";
import { Download, Send, Mail, Eye, X, Clock, CheckCircle } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HotelVoucherPanel({ hotel, onUpdate }) {
  const { getAuthHeaders } = useAuth();
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [sendDialog, setSendDialog] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState(hotel?.customer_email || "");
  const [loading, setLoading] = useState(null);
  const [msg, setMsg] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!hotel?.id) return;
    setLoadingHistory(true);
    try {
      const r = await axios.get(`${API}/hotels/${hotel.id}/voucher-history`, { headers: getAuthHeaders() });
      setHistory((r.data || []).slice().reverse()); // newest first
    } catch {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [hotel?.id]);

  useEffect(() => {
    fetchHistory();
    setRecipientEmail(hotel?.customer_email || "");
  }, [hotel]);

  useEffect(() => {
    return () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const showMsg = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  };

  const openCurrentPreview = async () => {
    setLoading("preview");
    try {
      const r = await axios.get(
        `${API}/hotels/${hotel.id}/preview-voucher`,
        { responseType: "blob", headers: getAuthHeaders() }
      );
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      setPreviewUrl(url);
    } catch {
      showMsg("Failed to load preview.", "error");
    } finally {
      setLoading(null);
    }
  };

  const openHistoricalPreview = async (gen) => {
    setLoading(`preview-${gen.id}`);
    try {
      const r = await axios.get(
        `${API}/hotels/${hotel.id}/voucher-history/${gen.id}/preview`,
        { responseType: "blob", headers: getAuthHeaders() }
      );
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      setPreviewUrl(url);
    } catch {
      showMsg("Failed to load historical voucher.", "error");
    } finally {
      setLoading(null);
    }
  };

  const generateAndDownload = async () => {
    setLoading("download");
    try {
      const r = await axios.post(
        `${API}/hotels/${hotel.id}/generate-voucher`,
        {},
        { responseType: "blob", headers: getAuthHeaders() }
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const disp = r.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^"]+)"?/);
      a.href = url;
      a.download = match ? match[1] : "hotel-voucher.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      await fetchHistory();
      if (onUpdate) onUpdate();
      showMsg("Voucher generated and downloaded.");
    } catch {
      showMsg("Failed to generate voucher.", "error");
    } finally {
      setLoading(null);
    }
  };

  const downloadHistorical = async (gen) => {
    setLoading(`dl-${gen.id}`);
    try {
      const r = await axios.get(
        `${API}/hotels/${hotel.id}/voucher-history/${gen.id}/download`,
        { responseType: "blob", headers: getAuthHeaders() }
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const disp = r.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^"]+)"?/);
      a.href = url;
      a.download = match ? match[1] : "hotel-voucher.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      showMsg("Failed to download historical voucher.", "error");
    } finally {
      setLoading(null);
    }
  };

  const sendVoucher = async (isTest = false) => {
    setLoading(isTest ? "test" : "send");
    setSendDialog(false);
    try {
      const r = await axios.post(`${API}/hotels/${hotel.id}/send-voucher`, {
        is_test: isTest,
        recipient_email: recipientEmail,
      }, { headers: getAuthHeaders() });
      await fetchHistory();
      if (onUpdate) onUpdate();
      showMsg(r.data.message || (isTest ? "Test email sent to admin." : `Voucher sent to ${recipientEmail}.`));
    } catch {
      showMsg("Failed to send voucher.", "error");
    } finally {
      setLoading(null);
    }
  };

  const latestGen = history[0] || null;

  return (
    <div className="space-y-5">
      {msg && (
        <div className={`rounded-lg px-4 py-3 text-sm font-semibold ${msg.type === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
          {msg.text}
        </div>
      )}

      {/* Voucher Actions */}
      <div className="bg-white border border-violet-100 rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-bold text-violet-800 uppercase tracking-wider mb-4">Voucher Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={generateAndDownload}
            disabled={!!loading}
            className="flex items-center gap-2 bg-violet-700 hover:bg-violet-800 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <Download size={15} />
            {loading === "download" ? "Generating..." : latestGen ? "Regenerate & Download" : "Generate & Download"}
          </button>

          <button
            onClick={() => setSendDialog(true)}
            disabled={!!loading}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <Send size={15} />
            Send to Customer
          </button>

          <button
            onClick={openCurrentPreview}
            disabled={!!loading}
            className="flex items-center gap-2 border border-violet-300 hover:bg-violet-50 text-violet-700 rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <Eye size={15} />
            {loading === "preview" ? "Loading..." : "Preview Current"}
          </button>

          <button
            onClick={() => sendVoucher(true)}
            disabled={!!loading}
            className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <Mail size={15} />
            {loading === "test" ? "Sending..." : "Test Email (Admin Only)"}
          </button>
        </div>

        {latestGen && (
          <div className="mt-4 text-xs text-gray-500 flex items-center gap-2 flex-wrap">
            <span>Latest generated {latestGen.generated_at?.slice(0, 10)}</span>
            {latestGen.sent && (
              <span className="text-green-600 font-semibold flex items-center gap-1">
                <CheckCircle size={11} />
                Sent to {latestGen.sent_to} on {latestGen.sent_at?.slice(0, 10)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Voucher History */}
      <div className="bg-white border border-violet-100 rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-bold text-violet-800 uppercase tracking-wider mb-4">Voucher History</h3>
        {loadingHistory ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-400">No vouchers generated yet. Use Generate & Download above.</p>
        ) : (
          <div className="space-y-2">
            {history.map((gen, idx) => (
              <div
                key={gen.id}
                className={`flex items-center justify-between rounded-lg p-3 border ${idx === 0 ? "bg-violet-50 border-violet-200" : "bg-gray-50 border-gray-100"}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {idx === 0 && (
                      <span className="text-xs font-bold text-violet-700 bg-violet-100 rounded-full px-2 py-0.5">Latest</span>
                    )}
                    <span className="text-xs text-gray-400">Generation #{gen.generation_number}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                    <Clock size={11} />
                    {gen.generated_at?.slice(0, 16).replace("T", " ")}
                  </p>
                  {gen.sent && (
                    <p className="text-xs text-green-600 font-semibold mt-0.5 flex items-center gap-1">
                      <CheckCircle size={11} />
                      Sent to {gen.sent_to} on {gen.sent_at?.slice(0, 10)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => openHistoricalPreview(gen)}
                    disabled={!!loading}
                    className="flex items-center gap-1 text-xs border border-violet-300 hover:bg-violet-50 text-violet-700 rounded-lg px-3 py-1.5 font-semibold disabled:opacity-50"
                  >
                    <Eye size={13} />
                    {loading === `preview-${gen.id}` ? "..." : "Preview"}
                  </button>
                  <button
                    onClick={() => downloadHistorical(gen)}
                    disabled={!!loading}
                    className="flex items-center gap-1 text-xs border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg px-3 py-1.5 font-semibold disabled:opacity-50"
                  >
                    <Download size={13} />
                    {loading === `dl-${gen.id}` ? "..." : "Download"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PDF Preview modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/60 flex flex-col z-50">
          <div className="flex items-center justify-between bg-white px-4 py-3 border-b border-gray-200 shrink-0">
            <span className="text-sm font-semibold text-gray-800">Hotel Voucher Preview</span>
            <button onClick={closePreview} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors">
              <X size={16} />
              Close
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <iframe
              src={previewUrl}
              title="Voucher Preview"
              className="w-full h-full border-0"
            />
          </div>
        </div>
      )}

      {/* Send dialog */}
      {sendDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Send Voucher to Customer</h3>
              <button onClick={() => setSendDialog(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              The current voucher PDF will be generated from today's booking data and attached to the email.
            </p>
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Recipient Email</label>
            <input
              type="email"
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-5"
              value={recipientEmail}
              onChange={e => setRecipientEmail(e.target.value)}
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setSendDialog(false)} className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2">
                Cancel
              </button>
              <button
                onClick={() => sendVoucher(false)}
                disabled={!recipientEmail || loading === "send"}
                className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-5 py-2 text-sm font-semibold disabled:opacity-50"
              >
                {loading === "send" ? "Sending..." : "Send Voucher"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
